import { useState, useEffect, useCallback } from "react";
import { apiClient } from "../App";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "./ui/dialog";
import { AlertTriangle, Archive, RotateCcw, Trash2, FileText, Filter, Database, History } from "lucide-react";
import { toast } from "sonner";

/**
 * OCI V2.6 — Data Management Centre.
 * Platform-Owner-only surface. Lives inside Platform Dashboard > Settings.
 *
 * Provides:
 *   - Organisation table with rollups + status
 *   - View / Archive / Restore / Delete actions
 *   - Pre-delete impact analysis with name-confirmation
 *   - Bulk demo/test cleanup (with keyword filters)
 *   - Past deletion reports viewer
 */
export function DataManagementCentre() {
  const [tab, setTab] = useState("orgs"); // orgs | bulk | reports
  return (
    <div data-testid="data-management-centre" className="space-y-4">
      <div className="flex items-center gap-2">
        <Database className="h-5 w-5 text-slate-700" />
        <h2 className="text-lg font-bold text-slate-900">Data Management</h2>
        <Badge variant="outline" className="ml-2 text-[10px]">Platform Owner only</Badge>
      </div>

      <div className="flex gap-2">
        {[
          { k: "orgs", label: "Organisations", icon: Database },
          { k: "bulk", label: "Bulk cleanup", icon: Filter },
          { k: "reports", label: "Deletion reports", icon: History },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)}
            data-testid={`dmc-tab-${t.k}`}
            className={`px-3 py-1.5 text-sm rounded-md border ${
              tab === t.k
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
            }`}>
            <t.icon className="inline h-3.5 w-3.5 mr-1" />
            {t.label}
          </button>
        ))}
      </div>

      {tab === "orgs" && <OrganisationTable />}
      {tab === "bulk" && <BulkCleanup />}
      {tab === "reports" && <DeletionReports />}
    </div>
  );
}

// ─── Organisation Table ─────────────────────────────────────────
function OrganisationTable() {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [impactOrg, setImpactOrg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("/data-mgmt/organisations");
      setOrgs(r.data || []);
    } catch (e) {
      toast.error("Failed to load organisations");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const archive = async (org) => {
    try {
      await apiClient.post(`/data-mgmt/organisations/${org.id}/archive`);
      toast.success(`${org.name} archived`);
      load();
    } catch (e) {
      toast.error("Archive failed: " + (e?.response?.data?.detail || e.message));
    }
  };
  const restore = async (org) => {
    try {
      await apiClient.post(`/data-mgmt/organisations/${org.id}/restore`);
      toast.success(`${org.name} restored`);
      load();
    } catch (e) {
      toast.error("Restore failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  if (loading) return <div className="text-sm text-slate-500" data-testid="dmc-loading">Loading…</div>;

  return (
    <div data-testid="dmc-org-table">
      <div className="text-xs text-slate-500 mb-2">
        {orgs.length} organisation{orgs.length === 1 ? "" : "s"}
      </div>
      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                <th className="px-3 py-2 font-medium text-slate-600">Name</th>
                <th className="px-3 py-2 font-medium text-slate-600">Created</th>
                <th className="px-3 py-2 font-medium text-slate-600">Status</th>
                <th className="px-3 py-2 font-medium text-slate-600 text-right">Campaigns</th>
                <th className="px-3 py-2 font-medium text-slate-600 text-right">Users</th>
                <th className="px-3 py-2 font-medium text-slate-600 text-right">Donors</th>
                <th className="px-3 py-2 font-medium text-slate-600">Last activity</th>
                <th className="px-3 py-2 font-medium text-slate-600 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {orgs.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-500"
                      data-testid="dmc-empty">No organisations</td></tr>
              )}
              {orgs.map((o) => (
                <tr key={o.id} className="border-b border-slate-100"
                    data-testid={`dmc-org-row-${o.id}`}>
                  <td className="px-3 py-2 font-medium">{o.name}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">
                    {o.createdAt ? new Date(o.createdAt).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-3 py-2">
                    <Badge variant={o.status === "archived" ? "outline" : "default"}
                           className="text-[10px]">
                      {o.status || "active"}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-right">{o.activeCampaigns}</td>
                  <td className="px-3 py-2 text-right">{o.activeUsers}</td>
                  <td className="px-3 py-2 text-right">{o.activeDonors}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">
                    {o.lastActivity ? new Date(o.lastActivity).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {o.status !== "archived" && (
                      <Button variant="ghost" size="sm" onClick={() => archive(o)}
                              data-testid={`dmc-archive-${o.id}`}>
                        <Archive className="h-3.5 w-3.5 mr-1" />
                        Archive
                      </Button>
                    )}
                    {o.status === "archived" && (
                      <Button variant="ghost" size="sm" onClick={() => restore(o)}
                              data-testid={`dmc-restore-${o.id}`}>
                        <RotateCcw className="h-3.5 w-3.5 mr-1" />
                        Restore
                      </Button>
                    )}
                    <Button variant="ghost" size="sm"
                            onClick={() => setImpactOrg(o)}
                            data-testid={`dmc-delete-${o.id}`}
                            className="text-rose-600 hover:text-rose-700 hover:bg-rose-50">
                      <Trash2 className="h-3.5 w-3.5 mr-1" />
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {impactOrg && (
        <DeleteConfirmDialog
          org={impactOrg}
          onClose={() => setImpactOrg(null)}
          onDone={() => { setImpactOrg(null); load(); }}
        />
      )}
    </div>
  );
}

// ─── Single-org delete dialog (impact + name confirmation) ──────
function DeleteConfirmDialog({ org, onClose, onDone }) {
  const [impact, setImpact] = useState(null);
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    apiClient.get(`/data-mgmt/organisations/${org.id}/impact`)
      .then((r) => setImpact(r.data))
      .catch((e) => toast.error("Impact analysis failed: " + (e?.response?.data?.detail || e.message)));
  }, [org.id]);

  const expected = impact?.confirmationToken || `DELETE: ${org.name}`;
  const matches = confirmation.trim() === expected;

  const execute = async () => {
    setBusy(true);
    try {
      const r = await apiClient.post(
        `/data-mgmt/organisations/${org.id}/delete`,
        { confirmation });
      toast.success(`Deleted ${org.name} — ${r.data.report.totalDeleted} docs removed`);
      onDone?.();
    } catch (e) {
      toast.error("Delete failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="dmc-delete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700">
            <AlertTriangle className="h-5 w-5" />
            Delete {org.name}
          </DialogTitle>
          <DialogDescription>
            This action permanently deletes the organisation and all associated operational data.
          </DialogDescription>
        </DialogHeader>

        {!impact ? (
          <div className="text-sm text-slate-500" data-testid="dmc-impact-loading">
            Computing impact…
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-sm font-medium" data-testid="dmc-impact-total">
              Total documents that will be deleted: <strong>{impact.totalAffectedDocs}</strong>
            </div>
            <div className="max-h-48 overflow-y-auto border border-slate-200 rounded p-2 text-xs"
                 data-testid="dmc-impact-counts">
              {Object.entries(impact.counts)
                .filter(([, v]) => v > 0)
                .sort((a, b) => b[1] - a[1])
                .map(([k, v]) => (
                <div key={k} className="flex justify-between py-0.5">
                  <span className="text-slate-600">{k}</span>
                  <span className="font-mono">{v}</span>
                </div>
              ))}
              {Object.values(impact.counts).every((v) => v === 0) && (
                <div className="text-slate-400 text-center py-2">No related rows</div>
              )}
            </div>
            <div className="space-y-1.5 pt-2 border-t border-slate-200">
              <label className="text-xs font-medium text-slate-700">
                Type the confirmation phrase below to enable deletion:
              </label>
              <code className="block bg-slate-100 px-2 py-1 rounded text-xs">{expected}</code>
              <Input
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
                placeholder={expected}
                data-testid="dmc-confirmation-input"
                className={matches ? "border-emerald-500" : ""}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="dmc-cancel">
            Cancel
          </Button>
          <Button variant="destructive" onClick={execute}
                  disabled={!matches || busy}
                  data-testid="dmc-confirm-delete">
            {busy ? "Deleting…" : "Permanently delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Bulk cleanup tool ──────────────────────────────────────────
function BulkCleanup() {
  const [keywords, setKeywords] = useState("TEST,DEMO,SANDBOX,DEV");
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState({});
  const [preview, setPreview] = useState(null);
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);

  const findCandidates = async () => {
    try {
      const r = await apiClient.get("/data-mgmt/bulk-cleanup/candidates",
        { params: { keywords } });
      setCandidates(r.data.matches || []);
      setSelected({});
      setPreview(null);
    } catch (e) {
      toast.error("Search failed");
    }
  };

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const selectedIds = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);

  const runPreview = async () => {
    if (!selectedIds.length) return;
    try {
      const r = await apiClient.post("/data-mgmt/bulk-cleanup/preview",
        { organisationIds: selectedIds });
      setPreview(r.data);
    } catch (e) {
      toast.error("Preview failed");
    }
  };

  const execute = async () => {
    if (confirmation !== "BULK-CLEANUP") {
      toast.error("Confirmation must be exactly: BULK-CLEANUP");
      return;
    }
    setBusy(true);
    try {
      const r = await apiClient.post("/data-mgmt/bulk-cleanup/execute",
        { organisationIds: selectedIds, confirmation });
      toast.success(`Bulk cleanup: ${r.data.executedCount} deleted, ${r.data.failedCount} failed`);
      setSelected({});
      setCandidates([]);
      setPreview(null);
      setConfirmation("");
    } catch (e) {
      toast.error("Bulk execute failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="dmc-bulk-cleanup">
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="text-sm font-medium">Keyword filter (comma-separated)</div>
          <div className="flex gap-2">
            <Input value={keywords} onChange={(e) => setKeywords(e.target.value)}
                   data-testid="dmc-bulk-keywords" />
            <Button onClick={findCandidates} data-testid="dmc-bulk-search">
              Find candidates
            </Button>
          </div>
          <div className="text-xs text-slate-500">
            Case-insensitive substring match on organisation name. No deletion happens at this step.
          </div>
        </CardContent>
      </Card>

      {candidates.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="text-sm font-medium mb-2">
              {candidates.length} candidate{candidates.length === 1 ? "" : "s"}
            </div>
            <div className="space-y-1 max-h-80 overflow-y-auto"
                 data-testid="dmc-bulk-candidates">
              {candidates.map((c) => (
                <label key={c.id}
                       className="flex items-center gap-3 p-2 border border-slate-200 rounded hover:bg-slate-50">
                  <input type="checkbox"
                         checked={!!selected[c.id]}
                         onChange={() => toggle(c.id)}
                         data-testid={`dmc-bulk-check-${c.id}`} />
                  <div className="flex-1">
                    <div className="text-sm font-medium">{c.name}</div>
                    <div className="text-xs text-slate-500">{c.id}</div>
                  </div>
                  {c.status === "archived" && (
                    <Badge variant="outline" className="text-[10px]">archived</Badge>
                  )}
                </label>
              ))}
            </div>
            <div className="flex gap-2 mt-3">
              <Button variant="outline" size="sm"
                      onClick={runPreview}
                      disabled={selectedIds.length === 0}
                      data-testid="dmc-bulk-preview-btn">
                Preview impact ({selectedIds.length})
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {preview && (
        <Card className="border-rose-200 bg-rose-50">
          <CardContent className="p-4 space-y-3">
            <div className="font-semibold text-rose-900">
              About to delete {preview.count} organisations
            </div>
            <div className="text-sm">
              Total documents impacted: <strong>{preview.totalAffectedDocs}</strong>
            </div>
            <div className="max-h-32 overflow-y-auto text-xs bg-white border border-rose-200 rounded p-2">
              {preview.organisations.map((o) => (
                <div key={o.id} className="flex justify-between py-0.5">
                  <span>{o.name}</span>
                  <span className="font-mono">{o.totalAffectedDocs}</span>
                </div>
              ))}
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-rose-900">
                Type <code className="bg-white px-1.5 py-0.5 rounded border">BULK-CLEANUP</code> to confirm:
              </label>
              <Input value={confirmation} onChange={(e) => setConfirmation(e.target.value)}
                     placeholder="BULK-CLEANUP"
                     data-testid="dmc-bulk-confirmation" />
            </div>
            <Button variant="destructive" onClick={execute}
                    disabled={confirmation !== "BULK-CLEANUP" || busy}
                    data-testid="dmc-bulk-execute">
              {busy ? "Deleting…" : "Execute bulk cleanup"}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─── Deletion reports viewer ────────────────────────────────────
function DeletionReports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get("/data-mgmt/deletion-reports")
      .then((r) => setReports(r.data || []))
      .catch(() => setReports([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-sm text-slate-500">Loading…</div>;
  if (!reports.length) {
    return (
      <Card data-testid="dmc-reports-empty">
        <CardContent className="p-6 text-center text-slate-500">
          <FileText className="h-8 w-8 mx-auto mb-2 text-slate-300" />
          No deletion reports yet.
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="space-y-2" data-testid="dmc-reports-list">
      {reports.map((r) => (
        <Card key={r.id} data-testid={`dmc-report-${r.id}`}>
          <CardContent className="p-3 text-sm">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">{r.organisationName}</div>
                <div className="text-xs text-slate-500">
                  {new Date(r.createdAt).toLocaleString()} ·
                  {" "}executed by {r.executorEmail || r.executorId} ·
                  {" "}<strong>{r.totalDeleted}</strong> docs deleted
                  {r.bulk && <Badge variant="outline" className="ml-2 text-[10px]">bulk</Badge>}
                </div>
              </div>
            </div>
            <details className="mt-2">
              <summary className="text-xs text-slate-600 cursor-pointer">View counts</summary>
              <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs mt-2 pl-2">
                {Object.entries(r.deletedCounts || {})
                  .filter(([, v]) => v > 0)
                  .sort((a, b) => b[1] - a[1])
                  .map(([k, v]) => (
                  <div key={k} className="flex justify-between">
                    <span className="text-slate-500">{k}</span>
                    <span className="font-mono">{v}</span>
                  </div>
                ))}
              </div>
            </details>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
